import React, { useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import Sidebar from '../components/Sidebar';
import ChatWindow from '../components/ChatWindow';
import { useChatStore } from '../store/chatStore';
import { useAuthStore } from '../store/authStore';

const Chat: React.FC = () => {
  const { chatId: urlChatId } = useParams<{ chatId?: string }>();
  const navigate = useNavigate();

  const loadChatsFromFirestore = useChatStore((s) => s.loadChatsFromFirestore);
  const chats = useChatStore((s) => s.chats);
  const activeChatId = useChatStore((s) => s.activeChatId);
  const setSidebarOpen = useChatStore((s) => s.setSidebarOpen);
  const isLoadingChats = useChatStore((s) => s.isLoadingChats);
  const user = useAuthStore((s) => s.user);

  const hasLoadedRef = useRef(false);
  // ✅ Prevent sync loops
  const syncingRef = useRef(false);

  // Load chats from Firestore once
  useEffect(() => {
    if (user && !hasLoadedRef.current) {
      hasLoadedRef.current = true;
      loadChatsFromFirestore(user.uid);
    }
  }, [user, loadChatsFromFirestore]);

  // ✅ FIX: Simplified URL sync - only runs when URL chatId changes
  useEffect(() => {
    if (isLoadingChats) return;
    if (syncingRef.current) return;

    if (urlChatId) {
      // Only sync if different from current active
      if (activeChatId !== urlChatId) {
        const chatExists = chats.some((c) => c.id === urlChatId);
        if (chatExists) {
          syncingRef.current = true;
          // Directly set state without pushState (URL already correct)
          useChatStore.setState({ activeChatId: urlChatId });
          // Allow next sync after this render cycle
          requestAnimationFrame(() => {
            syncingRef.current = false;
          });
        } else if (chats.length > 0) {
          // Chat doesn't exist and chats are loaded
          navigate('/', { replace: true });
        }
      }
    } else {
      // No URL chatId - clear active if needed
      if (activeChatId !== null) {
        syncingRef.current = true;
        useChatStore.setState({ activeChatId: null });
        requestAnimationFrame(() => {
          syncingRef.current = false;
        });
      }
    }
    // ✅ Remove activeChatId from deps to prevent loops
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [urlChatId, isLoadingChats, chats.length, navigate]);

  // Handle browser back/forward
  useEffect(() => {
    const handlePopState = () => {
      const path = window.location.pathname;
      const match = path.match(/^\/chat\/(.+)$/);
      if (match) {
        const id = match[1];
        const exists = useChatStore.getState().chats.some((c) => c.id === id);
        if (exists) {
          useChatStore.setState({ activeChatId: id });
        }
      } else {
        useChatStore.setState({ activeChatId: null });
      }
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  // Responsive sidebar
  useEffect(() => {
    if (window.innerWidth < 768) setSidebarOpen(false);
    const handleResize = () => {
      if (window.innerWidth < 768) setSidebarOpen(false);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.3 }}
      className="flex h-full overflow-hidden"
    >
      <Sidebar />
      <ChatWindow />
    </motion.div>
  );
};

export default Chat;